import { ITriggerRulesetType } from "types/ContractHierarchyRule/ContractHierarchyRule";

export interface IHierarchyRulesetOption {
  label: string;
  value: string;
  rulesetType: ITriggerRulesetType;
}

/** The only fields the option list reads off a ruleset row, whatever its type. `id` is optional
 *  because several of the ruleset types model it that way; a row without one is skipped. */
interface IScopedRuleset {
  id?: string;
  providerId?: string | null;
  // The four ruleset types do not agree on how they declare this (case rate types it as a
  // string), so the union stays wide and the strict comparison below does the filtering —
  // exactly what the inline `(r: any)` version this replaced did.
  payerId?: number | string | null;
  groupName?: string | null;
}

interface IBuildHierarchyRulesetOptionsInput {
  providerId: string;
  payerId: number;
  mppr?: IScopedRuleset[];
  caseRate?: IScopedRuleset[];
  packages?: IScopedRuleset[];
  /** CLE-2600 — charge-based rules are valid hierarchy triggers and suppression targets. */
  chargeBased?: IScopedRuleset[];
}

/**
 * Every ruleset on this provider+payer contract, as dropdown options.
 *
 * Feeds BOTH pickers on the page — the trigger dropdown for a new rule and the suppression
 * picker on an existing one — so a type added here becomes available in both at once.
 */
export function buildHierarchyRulesetOptions({
  providerId,
  payerId,
  mppr,
  caseRate,
  packages,
  chargeBased,
}: IBuildHierarchyRulesetOptionsInput): IHierarchyRulesetOption[] {
  const options: IHierarchyRulesetOption[] = [];

  const add = (
    rulesets: IScopedRuleset[] | undefined,
    rulesetType: ITriggerRulesetType,
    fallbackName: string
  ) => {
    (rulesets ?? [])
      .filter(
        (r) => !!r.id && r.providerId === providerId && r.payerId === payerId
      )
      .forEach((r) => {
        options.push({
          label: `${r.groupName || fallbackName} (${rulesetType})`,
          value: r.id as string,
          rulesetType,
        });
      });
  };

  add(mppr, "MPPR", "MPPR Ruleset");
  add(caseRate, "Case Rate", "Case Rate");
  add(packages, "Package", "Package");
  add(chargeBased, "Charge-Based Pricing", "Charge-Based Pricing");

  return options;
}
